import { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  // Check if on iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  useEffect(() => {
    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;

    if (isStandalone) {
      return; // Already installed, don't show prompt
    }

    // Check if user dismissed prompt recently (24 hours)
    const dismissedAt = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissedAt && Date.now() - parseInt(dismissedAt) < 24 * 60 * 60 * 1000) {
      return;
    }

    // Listen for beforeinstallprompt event (Chrome, Edge, etc.)
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after 30 seconds of browsing
      setTimeout(() => setShowPrompt(true), 30000);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // For iOS, show after 30 seconds
    if (isIOS) {
      setTimeout(() => setShowPrompt(true), 30000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, [isIOS]);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }

    if (!deferredPrompt) {
      console.error("Install prompt not available");
      toast.error("No se puede instalar en este momento");
      return;
    }

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      console.log(`User response to the install prompt: ${outcome}`);

      if (outcome === 'accepted') {
        console.log('PWA installed');
        toast.success("Instalando aplicación...", { duration: 5000 });
      } else {
        console.log('PWA installation dismissed');
        if (!isIOS) {
          toast.info("Si no aparece la ventana, revisa la barra de direcciones.", { duration: 5000 });
        }
      }
    } catch (err) {
      console.error("Error showing install prompt:", err);
      // Don't show generic error if it was just dismissed
      if (err.name !== 'NotAllowedError') {
        toast.error("Error al iniciar instalación: " + err.message);
      }
    }

    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setShowIOSInstructions(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  if (!showPrompt) return null;

  return (
    <>
      {/* Install Prompt Banner */}
      <div className="fixed bottom-20 left-4 right-4 z-50 animate-slide-up">
        <div className="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-2xl p-4 shadow-2xl">
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 text-black/60 hover:text-black"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-black rounded-xl flex items-center justify-center flex-shrink-0">
              <img
                src="/icons/icon-96x96.png"
                alt="ZT TACOS"
                className="w-10 h-10 rounded-lg"
              />
            </div>

            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-black text-lg">Instala ZT TACOS</h3>
              <p className="text-black/70 text-sm">
                Añade nuestra app a tu pantalla de inicio
              </p>
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <Button
              onClick={handleDismiss}
              variant="ghost"
              className="flex-1 text-black/70 hover:text-black hover:bg-black/10"
            >
              Ahora no
            </Button>
            <Button
              onClick={handleInstall}
              className="flex-1 bg-black text-yellow-500 hover:bg-black/90 gap-2"
            >
              <Download className="w-4 h-4" />
              Instalar
            </Button>
          </div>
        </div>
      </div>

      {/* iOS Instructions Modal */}
      {showIOSInstructions && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-end justify-center p-4">
          <div className="bg-gray-900 rounded-t-3xl w-full max-w-md p-6 animate-slide-up">
            <button
              onClick={handleDismiss}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="text-center mb-6">
              <Smartphone className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
              <h3 className="font-bold text-xl text-white">Instalar en iPhone/iPad</h3>
            </div>

            <div className="space-y-4 text-gray-300">
              <div className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-yellow-500 text-black flex items-center justify-center font-bold text-sm flex-shrink-0">1</span>
                <p>Pulsa el botón <strong className="text-white">Compartir</strong> en la barra de Safari (icono cuadrado con flecha)</p>
              </div>

              <div className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-yellow-500 text-black flex items-center justify-center font-bold text-sm flex-shrink-0">2</span>
                <p>Desplázate y pulsa <strong className="text-white">Añadir a pantalla de inicio</strong></p>
              </div>

              <div className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-yellow-500 text-black flex items-center justify-center font-bold text-sm flex-shrink-0">3</span>
                <p>Pulsa <strong className="text-white">Añadir</strong> en la esquina superior derecha</p>
              </div>
            </div>

            <Button
              onClick={handleDismiss}
              className="w-full mt-6 bg-yellow-500 text-black hover:bg-yellow-400"
            >
              Entendido
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
